import { useState, useEffect, Fragment, useRef, useCallback } from "react";
import "./styles.sass";
import assets from "../../assets";
import useHandler from "./handler";
import { ToggleButton } from "../../role-based-code/toggle-button";
import { QuizHero } from "../../components/quiz-hero";
import { LatexRenderer } from "../../components/latex-renderer";

const QuestionPalettePage = ({
  questions,
  selectedAnswers,
  getQuestionStatus,
  setCurrentQIndex,
  setShowPaletteModal,
  setIsQuizActive,
  onSubmitQuiz,
  submitLoading,
  setIsReviewMode,
}) => {
  const unansweredCount = questions?.filter(
    (_, i) => selectedAnswers[i] === undefined,
  ).length;

  return (
    <div className="palette_page_container">
      <div className="palette_page_content">
        <div className="palette_modal_header_main">
          <h3>Question Palette</h3>
          <p>
            You have {String(unansweredCount).padStart(2, "0")} unanswered
            questions. Click on any question number to review.
          </p>
        </div>

        <div className="palette_modal_subheader">
          <h4>Question Palette</h4>
          <div className="q_count_badge_premium">
            {questions?.length} Questions
          </div>
        </div>

        <div className="palette_modal_grid">
          {questions.map((_, index) => {
            const isAnswered = selectedAnswers[index] !== undefined;
            const className = isAnswered
              ? "palette_circle answered"
              : "palette_circle unoccupied";

            return (
              <div
                key={index}
                className={className}
                onClick={() => {
                  setCurrentQIndex(index);
                  setShowPaletteModal(false);
                  setIsReviewMode(true);
                }}
              >
                {String(index + 1).padStart(2, "0")}
              </div>
            );
          })}
        </div>

        <div className="palette_modal_legend">
          <div className="legend_item_premium">
            <div className="status_indicator answered"></div>
            <span>Answered</span>
          </div>
          <div className="legend_item_premium">
            <div className="status_indicator unoccupied"></div>
            <span>Unanswered</span>
          </div>
        </div>

        <div className="palette_modal_actions">
          <button
            className="p_cancel_btn"
            onClick={() => setShowPaletteModal(false)}
          >
            Cancel
          </button>
          <button
            className="p_submit_btn"
            onClick={() => {
              setShowPaletteModal(false);
              setIsQuizActive(false);
              onSubmitQuiz();
              setIsReviewMode(true);
            }}
          >
            {submitLoading && <div className="btn_spinner"></div>}
            Submit Quiz
          </button>
        </div>
      </div>
    </div>
  );
};

const QuestionPaletteSidebar = ({
  questions,
  selectedAnswers,
  getQuestionStatus,
  currentQIndex,
  setCurrentQIndex,
  isQuizSubmitted,
  onSubmitQuiz,
  submitLoading,
}) => {
  return (
    <section className="palette_sidebar_premium">
      <div className="sidebar_palette_header">
        <span>Question Palette</span>
        <div className="q_badge_mini">{questions?.length} Questions</div>
      </div>

      <div className="sidebar_palette_grid">
        {questions.map((_, index) => {
          const status = getQuestionStatus(index);
          const isAnsweredQ = selectedAnswers[index] !== undefined;

          let className = "palette_circle_mini";
          if (isAnsweredQ) {
            className += " answered";
            if (index === currentQIndex) className += " current_answered";
          } else if (index === currentQIndex) {
            className += " current";
          } else if (status === "not-answered") {
            className += " not_answered";
          } else {
            className += " unoccupied";
          }

          return (
            <div
              key={index}
              className={className}
              onClick={() => setCurrentQIndex(index)}
            >
              {String(index + 1).padStart(2, "0")}
            </div>
          );
        })}
      </div>

      <div className="sidebar_status_legend">
        <div className="legend_item_mini">
          <div className="status_dot current"></div>
          <span>Current</span>
        </div>
        <div className="legend_item_mini">
          <div className="status_dot answered"></div>
          <span>Answered</span>
        </div>
        <div className="legend_item_mini">
          <div className="status_dot unoccupied"></div>
          <span>Unanswered</span>
        </div>
      </div>
      {!isQuizSubmitted && (
        <button
          style={{
            width: "100%",
            marginTop: "auto",
            background: "#0f172a",
            color: "#fff",
            padding: "14px",
            borderRadius: "8px",
            fontWeight: "bold",
            cursor: "pointer",
            border: "none",
          }}
          onClick={onSubmitQuiz}
        >
          {submitLoading ? "Submitting..." : "Submit Quiz"}
        </button>
      )}
    </section>
  );
};

const QuizHSlider = ({
  questions,
  renderCard,
  currentQIndex,
  setCurrentQIndex,
}) => {
  const trackRef    = useRef(null);
  const rafIdRef    = useRef(null);
  const cooldownRef = useRef(false);
  const wheelAccRef  = useRef(0);      // accumulated deltaX within one gesture
  const wheelTimerRef = useRef(null);  // debounce to reset accumulator on silence
  const [isReady, setIsReady] = useState(false);

  const ease = (t) => 1 - Math.pow(1 - t, 3);

  const animateTo = useCallback((track, targetLeft) => {
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    const start    = track.scrollLeft;
    const dist     = targetLeft - start;
    if (Math.abs(dist) < 1) { cooldownRef.current = false; return; }
    const duration  = 360;
    const startTime = performance.now();
    const step = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      track.scrollLeft = start + dist * ease(p);
      if (p < 1) {
        rafIdRef.current = requestAnimationFrame(step);
      } else {
        track.scrollLeft = targetLeft;
        rafIdRef.current = null;
        setTimeout(() => { cooldownRef.current = false; }, 80);
      }
    };
    rafIdRef.current = requestAnimationFrame(step);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToIndex = useCallback((idx, track) => {
    const t = track || trackRef.current;
    if (!t) return;
    const card = t.children[idx];
    if (!card) return;
    const target =
      t.clientWidth > 0
        ? card.offsetLeft - (t.clientWidth - card.offsetWidth) / 2
        : idx === 0
          ? 0
          : card.offsetLeft;
    animateTo(t, Math.max(0, target));
  }, [animateTo]);

  useEffect(() => {
    setIsReady(false);
    cooldownRef.current = true;
    goToIndex(currentQIndex);
    const timer = setTimeout(() => {
      setIsReady(true);
      cooldownRef.current = false;
    }, 1000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions.length]);

  useEffect(() => {
    if (!isReady) return;
    cooldownRef.current = true;
    goToIndex(currentQIndex);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQIndex]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!isReady || cooldownRef.current) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.85) {
            const idx = Array.from(track.children).indexOf(entry.target);
            if (idx >= 0) setCurrentQIndex(idx);
          }
        });
      },
      { root: track, threshold: 0.85 },
    );
    Array.from(track.children).forEach((c) => observer.observe(c));
    return () => observer.disconnect();
  }, [questions.length, setCurrentQIndex]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onWheel = (e) => {
      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);
      if (absY >= absX) return;
      e.preventDefault();
      // Debounce: reset accumulator 200ms after gesture ends
      clearTimeout(wheelTimerRef.current);
      wheelTimerRef.current = setTimeout(() => { wheelAccRef.current = 0; }, 200);
      // While animating, swallow all events without accumulating
      if (cooldownRef.current) return;
      wheelAccRef.current += e.deltaX;
      // Require 60px of intentional scroll before advancing one card
      if (Math.abs(wheelAccRef.current) < 60) return;
      const direction = wheelAccRef.current > 0 ? 1 : -1;
      wheelAccRef.current = 0;
      cooldownRef.current = true;
      const maxIdx = questions.length - 1;
      setCurrentQIndex((prev) => {
        const next = direction > 0
          ? Math.min(prev + 1, maxIdx)
          : Math.max(prev - 1, 0);
        if (next !== prev) goToIndex(next, track);
        else { setTimeout(() => { cooldownRef.current = false; }, 80); }
        return next;
      });
    };
    track.addEventListener("wheel", onWheel, { passive: false });
    return () => track.removeEventListener("wheel", onWheel);
  }, [questions.length, goToIndex, setCurrentQIndex]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (cooldownRef.current) return;
      cooldownRef.current = true;
      const maxIdx = questions.length - 1;
      const track = trackRef.current;
      setCurrentQIndex((prev) => {
        const next = e.key === "ArrowRight"
          ? Math.min(prev + 1, maxIdx)
          : Math.max(prev - 1, 0);
        if (next !== prev) goToIndex(next, track);
        else { setTimeout(() => { cooldownRef.current = false; }, 80); }
        return next;
      });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [questions.length, goToIndex, setCurrentQIndex]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let startX = 0, startY = 0;
    const onTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      const dx = startX - e.changedTouches[0].clientX;
      const dy = startY - e.changedTouches[0].clientY;
      if (Math.abs(dx) < Math.abs(dy) || Math.abs(dx) < 30) return;
      if (cooldownRef.current) return;
      cooldownRef.current = true;
      const maxIdx = questions.length - 1;
      setCurrentQIndex((prev) => {
        const next = dx > 0
          ? Math.min(prev + 1, maxIdx)
          : Math.max(prev - 1, 0);
        if (next !== prev) goToIndex(next, track);
        else { setTimeout(() => { cooldownRef.current = false; }, 80); }
        return next;
      });
    };
    track.addEventListener("touchstart", onTouchStart, { passive: true });
    track.addEventListener("touchend",   onTouchEnd,   { passive: true });
    return () => {
      track.removeEventListener("touchstart", onTouchStart);
      track.removeEventListener("touchend",   onTouchEnd);
    };
  }, [questions.length, goToIndex, setCurrentQIndex]);

  return (
    <section className="quiz_hslider_wrapper">
      <div className="quiz_hslider_track" ref={trackRef}>
        {questions.map((_, qIndex) => (
          <div
            className={`quiz_hslider_card${qIndex === currentQIndex ? " is_active" : ""}`}
            key={qIndex}
          >
            <div className="quiz_hslider_card_inner">
              {renderCard(qIndex)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export const Quiz = () => {
  const {
    user,
    openDropdown,
    subjectRef,
    chapterRef,
    selectedChapter,
    setSelectedChapter,
    selectedSubject,
    panelOpen,
    chapters,
    subjects,
    isStartEnabled,
    currentQuestion,
    selectedOption,
    questions,
    currentQIndex,
    questionDetails,
    isAnswered,
    isCorrect,
    markDetails,
    showMarks,
    setShowMarks,
    isQuizActive,
    selectedLesson,
    selectedPart,
    selectedUnit,
    partRef,
    lessonRef,
    unitRef,
    questionRef,
    parts,
    lessons,
    units,
    isLoading,
    selectChapters,
    mediums,
    mediumRef,
    selectedMedium,
    selectLessons,
    hidePanals,
    mode,
    selectedQuestion,
    numberOfQuestion,
    selectedAnswers,
    submitLoading,
    timeRemaining,
    setTimeRemaining,
    setCurrentQIndex,
    setSelectedQuestion,
    setSelectedLesson,
    setSelectedPart,
    setSelectedUnit,
    setSelectedChapters,
    setIsReviewMode,
    isReviewMode,
    setPanelOpen,
    setOpenDropdown,
    setSelectedSubject,
    onStartQuiz,
    handleNext,
    handlePrevious,
    handleOptionSelect,
    onSubmitQuiz,
    onCancel,
    onRetakeQuiz,
    setIsQuizActive,
    getQuizDuration,
    setSelectedMedium,
    setSelectedLessons,
    setHidePanals,
    onToggleQuizBtn,
    getQuestionStatus,
    showSetupPopup,
    setShowSetupPopup,
    showPaletteModal,
    setShowPaletteModal,
  } = useHandler();

  const totalQuestions = questions?.length;
  const isFullySubmitted = !!markDetails?.DETAILS?.length;

  const [slideDirection, setSlideDirection] = useState("right");
  const [animKey, setAnimKey] = useState(0);

  const QuizSetupPopup = () => {
    const durationMin = getQuizDuration(selectLessons, selectedQuestion) / 60;
    // const qCount =
    //   selectLessons?.includes("All Lessons") || selectLessons?.length > 1
    //     ? 25
    //     : 10;
    const qCount = selectedQuestion || 10;

    return (
      <div className="quiz_setup_overlay">
        <div className="quiz_setup_card">
          <div className="quiz_setup_header">
            <h3>Quiz Setup</h3>
            <p>Review your quiz configuration</p>
          </div>

          <div className="quiz_setup_body">
            <div className="setup_section">
              <div className="setup_icon_wrapper subject_p_icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              </div>
              <div className="setup_info">
                <span className="setup_label">Subject</span>
                <span className="setup_value">
                  {selectedPart} - {selectedUnit}
                </span>
              </div>
            </div>

            <div className="setup_section">
              <div className="setup_icon_wrapper chapter_p_icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </div>
              <div className="setup_info">
                <span className="setup_label">Selected Lessons</span>
                <ul className="setup_list_p">
                  {(selectLessons || []).map((chapter, idx) => (
                    <li key={idx}>{chapter}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="setup_stats_row">
              <div className="setup_stat_box questions_box_p">
                <div className="stat_icon quiz_icon_p">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <div className="stat_labels">
                  <span className="stat_label">QUESTIONS</span>
                  <span className="stat_value">{qCount}</span>
                </div>
              </div>

              <div className="setup_stat_box duration_box_p">
                <div className="stat_icon time_icon_p">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div className="stat_labels">
                  <span className="stat_label">DURATION</span>
                  <span className="stat_value">{durationMin}min</span>
                </div>
              </div>
            </div>

            <div className="setup_note_p">
              <div className="note_icon_p">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <p>
                Once you start the quiz, the timer will begin automatically.
                Make sure you're ready! Before final submission, you can review
                your answers and complete any unanswered questions.
              </p>
            </div>
          </div>

          <div className="quiz_setup_footer">
            <button
              className="setup_cancel_btn"
              onClick={() => setShowSetupPopup(false)}
            >
              Cancel
            </button>
            <button
              className="setup_start_btn"
              onClick={() => {
                setShowSetupPopup(false);
                setHidePanals(true);
                setIsQuizActive(true);
                onStartQuiz();
              }}
            >
              Start Quiz
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (questions.length > 0 && isQuizActive) {
      const duration = getQuizDuration(selectLessons, selectedQuestion);
      setTimeRemaining(duration);
    }
  }, [questions.length, isQuizActive]);

  useEffect(() => {
    if (!isQuizActive || questions.length === 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsQuizActive(false);
          onSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isQuizActive, questions.length]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const correctCount = markDetails?.DETAILS?.filter(
    ({ IS_CORRECT }) => IS_CORRECT === true,
  ).length;
  const wrongCount = markDetails?.DETAILS?.filter(
    ({ USER_ANSWER, IS_CORRECT }) => USER_ANSWER && IS_CORRECT === false,
  ).length;
  const notAttemptedCount = markDetails?.DETAILS?.filter(
    (USER_ANSWER) => !USER_ANSWER,
  ).length;

  const scorePercentage = Math.round(
    (markDetails?.TOTAL_MARKS / markDetails?.MAX_MARKS) * 100,
  );

  const notAnswerdCount = markDetails?.NOT_ATTEMPTED_COUNT;

  const renderScoreCardDetails = (item, index) => {
    const { QUESTION, IS_CORRECT, USER_ANSWER, CORRECT_ANSWER, REMARK } =
      item || {};

    return (
      <Fragment key={index}>
        {IS_CORRECT === true ? (
          <section
            className="questions_container1"
          >
            <div className="status_icon_wrapper correct">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <section className="questions_content">
              <h4>Question {index + 1}</h4>
              <h5><LatexRenderer text={QUESTION} /></h5>
              <div className="ans_comparison_grid">
                <div className="ans_card correct_answer">
                  <span className="label">Correct Answer</span>
                  <div className="value"><LatexRenderer text={CORRECT_ANSWER} /></div>
                </div>
              </div>
              {REMARK && (
                <div className="ans_box explanation">
                  <span className="label">Remark</span>
                  <div className="value"><LatexRenderer text={REMARK} /></div>
                </div>
              )}
            </section>
          </section>
        ) : (
          <section
            className="questions_container3"
          >
            <div className="status_icon_wrapper wrong">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
            <section className="questions_content">
              <h4>Question {index + 1}</h4>
              <h5><LatexRenderer text={QUESTION} /></h5>
              <section className="ans_comparison_grid">
                <div className="ans_card your_answer">
                  <span className="label">Your Answer</span>
                  <div className="value"><LatexRenderer text={USER_ANSWER || "Not Attempted"} /></div>
                </div>
                <div className="ans_card correct_answer">
                  <span className="label">Correct Answer</span>
                  <div className="value"><LatexRenderer text={CORRECT_ANSWER} /></div>
                </div>
              </section>
              {REMARK && (
                <div className="ans_box explanation">
                  <span className="label">Explanation</span>
                  <div className="value"><LatexRenderer text={REMARK} /></div>
                </div>
              )}
            </section>
          </section>
        )}
      </Fragment>
    );
  };

  return (
    <Fragment>
      {showSetupPopup && <QuizSetupPopup />}
      <section className="chat_layout">
        {!hidePanals && (
          <section className="nav_context_contanier">
            <section className="nav_learn_title">
              <h4>Learning Context</h4>
              <h6>Set your topic to start learning</h6>
            </section>

            <section className="nav_subject_details">
              <section className="board_wrapper">
                <section className="class">
                  <h5>Competitive Exam</h5>
                  <h4>{user?.data?.class}</h4>
                </section>
              </section>
            </section>

            <section className="quiz_nav_mode_wrapper">
              <section className="quiz_nav_mode_container">
                <h4>Instant Mode</h4>
                <ToggleButton isToggle={mode} onToggle={onToggleQuizBtn} />
                <h4>Review Mode</h4>
              </section>
            </section>

            <section className="quiz_default_medium_wrapper">
              <h4>Medium</h4>
              <h5>{user?.data?.medium}</h5>
            </section>

            <section className="nav_subject_wrapper">
              {/* MEDIUM */}
              {/* <section
                className="desc_subject_wrapper"
                ref={mediumRef}
                onClick={() => setOpenDropdown("medium")}
              >
                <h4>Medium</h4>
                <section className="desc_drop_down_wrapper">
                  <p>{selectedMedium || "Select Medium"}</p>
                  <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
                </section>
                {openDropdown === "medium" && (
                  <section className="desc_options_list">
                    {isLoading ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Loading mediums...
                      </p>
                    ) : mediums?.length > 0 ? (
                      mediums?.map((item, i) => (
                        <p
                          key={i}
                          className="desc_option_item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMedium(item);
                            setOpenDropdown(null);
                          }}
                        >
                          {item}
                        </p>
                      ))
                    ) : (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "red",
                        }}
                      >
                        No mediums found
                      </p>
                    )}
                    {}
                  </section>
                )}
              </section> */}

              {/* SUBJECT */}
              {/* <section
              className="desc_subject_wrapper"
              ref={partRef}
              onClick={() => setOpenDropdown("part")}
            >
              <h4>Part</h4>
              <section className="desc_drop_down_wrapper">
                <p>{selectedPart || "Select Part"}</p>
                <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
              </section>
              {openDropdown === "part" && (
                <section className="desc_options_list">
                  {!selectedMedium ? ( <p
                      style={{
                        textAlign: "center",
                        marginTop: "35%",
                        color: "green",
                      }}
                    >
                      Please first Choose Medium{" "}
                    </p>) : isLoading ? (<p
                      style={{
                        textAlign: "center",
                        marginTop: "35%",
                        color: "green",
                      }}
                    >
                      Loading parts...
                    </p>) :  parts?.length > 0 ? (
                    parts?.map((item, i) => (
                      <p
                        key={i}
                        className="desc_option_item"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSubject(item);
                          setOpenDropdown(null);
                        }}
                      >
                        {item}
                      </p>
                    ))
                  ): (<p
                      style={{
                        textAlign: "center",
                        marginTop: "35%",
                        color: "red",
                      }}
                    >
                      No parts found
                    </p>)}
                </section>
              )}
            </section> */}
              <section
                className="desc_subject_wrapper"
                ref={partRef}
                onClick={() => setOpenDropdown("part")}
              >
                <h4>Part</h4>
                <section className="desc_drop_down_wrapper">
                  <p>{selectedPart || "Select Part"}</p>
                  <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
                </section>
                {openDropdown === "part" && (
                  <section className="desc_options_list">
                    {isLoading ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Loading parts...
                      </p>
                    ) : parts?.length > 0 ? (
                      parts?.map((item, i) => (
                        <p
                          key={i}
                          className="desc_option_item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPart(item);
                            setOpenDropdown(null);
                          }}
                        >
                          {item}
                        </p>
                      ))
                    ) : (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "red",
                        }}
                      >
                        No parts found
                      </p>
                    )}
                  </section>
                )}
              </section>

              <section
                className="desc_subject_wrapper"
                ref={unitRef}
                onClick={() => setOpenDropdown("unit")}
              >
                <h4>Unit</h4>
                <section className="desc_drop_down_wrapper">
                  <p>{selectedUnit || "Select Unit"}</p>
                  <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
                </section>
                {openDropdown === "unit" && (
                  <section className="desc_options_list">
                    {!selectedPart ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Please first Choose Part
                      </p>
                    ) : isLoading ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Loading units...
                      </p>
                    ) : units.length > 0 ? (
                      units?.map((item, i) => (
                        <p
                          key={i}
                          className="desc_option_item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedUnit(item);
                            setOpenDropdown(null);
                          }}
                        >
                          {item}
                        </p>
                      ))
                    ) : (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "red",
                        }}
                      >
                        No units found
                      </p>
                    )}
                  </section>
                )}
              </section>

              {/* CHAPTER */}
              <section
                className="desc_subject_wrapper"
                ref={chapterRef}
                onClick={() => setOpenDropdown("chapter")}
              >
                <h4>Chapter</h4>
                <section className="desc_drop_down_wrapper">
                  <p>{selectedChapter || "Select Chapter"}</p>
                  <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
                </section>
                {openDropdown === "chapter" && (
                  <section className="desc_options_list">
                    {!selectedUnit ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Please first Choose Unit
                      </p>
                    ) : isLoading ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Loading chapters...
                      </p>
                    ) : chapters?.length > 0 ? (
                      chapters?.map((item, i) => (
                        <p
                          key={i}
                          className="desc_option_item"
                          onClick={(e) => {
                            // e.stopPropagation();
                            // setSelectedChapters(item);
                            // setOpenDropdown(null);
                            e.stopPropagation();
                            setSelectedChapter(item);
                            setSelectedChapters([item]);
                            setOpenDropdown(null);
                          }}
                        >
                          {item}
                        </p>
                      ))
                    ) : (
                      <p className="desc_option_item">No chapters found</p>
                    )}
                    {/* {selectedSubject == "" && <p style={{textAlign:"center",marginTop:"35%",color:"green"}}>Please first Choose Subject</p>}
                  {} */}
                  </section>
                )}
              </section>
              <section
                className="desc_subject_wrapper"
                ref={lessonRef}
                onClick={() => setOpenDropdown("lesson")}
              >
                <h4>Lesson</h4>
                <section className="desc_drop_down_wrapper">
                  {/* <p>{selectedLesson || "Select Lessons"}</p> */}
                  <section className="selected_chapter_list">
                    {selectLessons?.length === 0 ? (
                      <p>Select Lessons</p>
                    ) : (
                      selectLessons?.map((item, index) => {
                        return (
                          <section key={index} className="items">
                            <p>{item}</p>
                            <img
                              src={assets.ic_close_white}
                              alt="ic_close_white"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedLessons((prev) => {
                                  if (prev.includes(item)) {
                                    return prev.filter((c) => c !== item);
                                  } else {
                                    return [...prev, item];
                                  }
                                });
                              }}
                              style={{
                                cursor: "pointer",
                                width: "14px",
                                height: "14px",
                              }}
                            />
                          </section>
                        );
                      })
                    )}
                  </section>
                  <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
                </section>
                {openDropdown === "lesson" && (
                  <section className="desc_options_list">
                    {!selectedChapter ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Please first Choose Chapter
                      </p>
                    ) : isLoading ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Loading lessons...
                      </p>
                    ) : lessons.length > 0 ? (
                      lessons?.map((item, i) => (
                        <p
                          key={i}
                          className={`option_item ${selectLessons.includes(item) ? "selected" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLesson(item);
                            setSelectedLessons((prev) => {
                              // if (prev.includes(item)) {
                              //   // remove if already selected
                              //   return prev.filter((c) => c !== item);
                              // } else {
                              //   // add if not selected
                              //   return [...prev, item];
                              // }
                              if (item === "All Lessons") {
                                if (prev.includes("All Lessons")) {
                                  return [];
                                }
                                return ["All Lessons"];
                              }
                              let updated = prev.filter(
                                (c) => c !== "All Lessons",
                              );
                              if (updated.includes(item)) {
                                updated = updated.filter((c) => c !== item);
                              } else {
                                updated = [...updated, item];
                              }
                              return updated;
                            });
                            setOpenDropdown(null);
                          }}
                        >
                          {item}
                          {selectLessons.includes(item) && ` \u2713`}
                        </p>
                      ))
                    ) : (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "red",
                        }}
                      >
                        No lessons found
                      </p>
                    )}
                  </section>
                )}
              </section>
              {/* NO OF QUESTIONS */}
              <section
                className="desc_subject_wrapper"
                ref={questionRef}
                onClick={() => setOpenDropdown("question")}
              >
                <h4>No.of questions</h4>
                <section className="desc_drop_down_wrapper">
                  <p>{selectedQuestion || "Select question"}</p>
                  <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
                </section>
                {openDropdown === "question" && (
                  <section className="desc_options_list">
                    {!selectLessons ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Please first Choose Lesson
                      </p>
                    ) : isLoading ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Loading no of questions...
                      </p>
                    ) : numberOfQuestion?.length > 0 ? (
                      numberOfQuestion?.map((item, i) => (
                        <p
                          key={i}
                          className="desc_option_item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedQuestion(item);
                            setOpenDropdown(null);
                          }}
                        >
                          {item}
                        </p>
                      ))
                    ) : (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "red",
                        }}
                      >
                        No question found
                      </p>
                    )}
                  </section>
                )}
              </section>

              <button
                className="start_quiz_wrapper"
                disabled={!isStartEnabled}
                onClick={() => setShowSetupPopup(true)}
              >
                {/* Start quiz */}
                Evaluate Me
              </button>
            </section>
          </section>
        )}

        {/* quiz */}
        <section className="chat-panal">
          {!isQuizActive &&
          !markDetails?.DETAILS?.length &&
          questions?.length === 0 ? (
            <QuizHero />
          ) : (
            <Fragment>
              <div className="quiz-header-premium">
                <div className="header-left">
                  <button className="back-btn" onClick={onCancel}>
                    <svg
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                  </button>
                  <div className="breadcrumbs">
                    <h3>{selectedSubject}</h3>
                    <p>
                      {selectedChapter}
                    </p>
                  </div>
                </div>
                <div className="header-right">
                  <div className="question-counter">
                    <span className="label">QUESTION</span>
                    <span className="count">
                      <b>{String(currentQIndex + 1).padStart(2, "0")}</b> /{" "}
                      {totalQuestions}
                    </span>
                  </div>
                  {!isReviewMode && !markDetails?.DETAILS?.length && questions?.length > 0 && (
                    <div className="nav_hslider_dots">
                      {questions.map((_, i) => (
                        <span
                          key={i}
                          className={`hslider_dot${i === currentQIndex ? " active" : ""}`}
                          onClick={() => setCurrentQIndex(i)}
                        />
                      ))}
                    </div>
                  )}
                  <div className="timer-pill-premium">
                    <svg
                      width="18"
                      height="18"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{formatTime(timeRemaining)}</span>
                  </div>

                </div>
              </div>
              {/* chat body */}
              <section className="chat_body_quiz">
                <section className="quiz-main-container">
                  {showPaletteModal ? (
                    <QuestionPalettePage
                      questions={questions}
                      selectedAnswers={selectedAnswers}
                      getQuestionStatus={getQuestionStatus}
                      setCurrentQIndex={setCurrentQIndex}
                      setShowPaletteModal={setShowPaletteModal}
                      setIsQuizActive={setIsQuizActive}
                      onSubmitQuiz={onSubmitQuiz}
                      submitLoading={submitLoading}
                      setIsReviewMode={setIsReviewMode}
                    />
                  ) : showMarks && markDetails?.DETAILS?.length ? (
                    <Fragment>
                      <section className="award_container">
                        <img
                          src={assets.ic_award}
                          alt="award"
                          className="award_img"
                        />
                        <h6 className="compelete_text">You've successfully completed the assessment</h6>
                        <h5 className="select_chapter">{selectedChapter}</h5>
                        <div className="syllabus">
                          <span className="badge_pill">{user?.data?.board}</span>
                          <span className="dot_sep"></span>
                          <span className="badge_pill">Group 4</span>
                          <span className="dot_sep"></span>
                          <span className="badge_pill subject">{selectedSubject}</span>
                        </div>
                        <section className="score_box_container">
                          <section className="score_box_wrapper">
                            <img src={assets.ic_score} alt="ic_score" />
                            <h3>{`${scorePercentage}%`}</h3>
                            <h5>Score</h5>
                          </section>
                          <section className="score_box_wrapper">
                            <img src={assets.ic_correct} alt="ic_score" />
                            <h3>{correctCount}</h3>
                            <h5>Correct</h5>
                          </section>
                          <section className="score_box_wrapper">
                            <img src={assets.ic_wrong} alt="ic_wrong" />
                            <h3>{wrongCount}</h3>
                            <h5>In Correct</h5>
                          </section>
                          <section className="score_box_wrapper">
                            <img src={assets.ic_unattempt} alt="ic_unattempt" />
                            <h3>{notAnswerdCount}</h3>
                            <h5>Not Attempted</h5>
                          </section>
                        </section>
                        <section className="questions_overview_wrapper">
                          <h4>Question by Question Review</h4>
                          <h6>Detailed breakdown of each question</h6>
                          {markDetails?.DETAILS.map(renderScoreCardDetails)}
                        </section>
                        <section className="quiz_btns_wrapper">
                          <button className="retake_btn" onClick={onRetakeQuiz}>
                            <svg
                              width="20"
                              height="20"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                              />
                            </svg>
                            <span>Retake Quiz</span>
                          </button>
                          <button className="back_menu_btn" onClick={onCancel}>
                            <svg
                              width="20"
                              height="20"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M10 19l-7-7m0 0l7-7m-7 7h18"
                              />
                            </svg>
                            <span>Back to Main Menu</span>
                          </button>
                        </section>
                      </section>
                    </Fragment>
                  ) : (
                    <div
                      className={
                        isReviewMode
                          ? "quiz_review_layout"
                          : "quiz_normal_layout"
                      }
                    >
                      <div className="review_main_content">
                        {(() => {
                          const showStack =
                            !isReviewMode &&
                            !markDetails?.DETAILS?.length &&
                            questions?.length > 0;

                          const renderQuestionCardInner = (qIndex) => {
                            const q = questions[qIndex];
                            if (!q) return null;
                            const selOpt = selectedAnswers[qIndex];
                            const isAnsweredQ = selOpt !== undefined;
                            const isCorrectQ =
                              isAnsweredQ &&
                              q?.options[selOpt] === q?.answer;
                            return (
                              <Fragment>
                                <section className="question-section">
                                  <div className="question_badges">
                                    <span className="badge_mcq">
                                      Multiple Choice
                                    </span>
                                  </div>
                                  <h3 className="question-title"><LatexRenderer text={q?.question} /></h3>
                                  {(isFullySubmitted || (isAnsweredQ && !mode)) && (
                                    <div
                                      className={`answer-feedback ${
                                        isCorrectQ ? "correct" : "wrong"
                                      }`}
                                    >
                                      {isCorrectQ ? (
                                        <>
                                          <div className="feedback_icon correct">
                                            {"\u2713"}
                                          </div>
                                          <span>Correct Answer!</span>
                                        </>
                                      ) : (
                                        <>
                                          <div className="feedback_icon wrong">
                                            {"\u2715"}
                                          </div>
                                          <span>Incorrect Answer!</span>
                                        </>
                                      )}
                                    </div>
                                  )}

                                  {(isFullySubmitted || (isAnsweredQ && !mode)) &&
                                    !isCorrectQ && (
                                      <section className="explaination_full_wrapper">
                                        <img
                                          src={assets.ic_wrong_close}
                                          alt="wrong"
                                        />
                                        <section className="explaination_wrapper">
                                          <h4>Incorrect</h4>
                                          <h5>{`The correct answer is ${questionDetails[qIndex]?.ANSWER}`}</h5>
                                          <section className="explaination_container">
                                            <h5>Explanation:</h5>
                                            <h6>
                                              {
                                                questionDetails[qIndex]
                                                  ?.EXPLANATION
                                              }
                                            </h6>
                                          </section>
                                        </section>
                                      </section>
                                    )}




                                  <section className="options-section">
                                    {q?.options?.map((option, index) => (
                                      <label
                                        key={index}
                                        className={`option_quiz ${
                                          selOpt === index
                                            ? isFullySubmitted || (isAnsweredQ && !mode)
                                              ? isCorrectQ
                                                ? "option-correct"
                                                : "option-wrong"
                                              : "selected"
                                            : ""
                                        } ${(isFullySubmitted || (isAnsweredQ && !mode)) && index === q.options.indexOf(q.answer) ? "option-correct" : ""}`}

                                      >
                                        <input
                                          type="radio"
                                          name={`quiz-option-${qIndex}`}
                                          checked={selOpt === index}
                                          disabled={
                                            isFullySubmitted ||
                                            selOpt !== undefined && !mode ||
                                            (showStack && qIndex !== currentQIndex)
                                          }
                                          onChange={() =>
                                            handleOptionSelect(index, qIndex)
                                          }
                                        />
                                         <section className="option-text">
                                           <span className="option-letter">
                                             {String.fromCharCode(65 + index)}
                                             .{" "}
                                           </span>
                                           <LatexRenderer text={option} />
                                         </section>
                                      </label>
                                    ))}
                                  </section>
                                </section>
                                {!isReviewMode && !isFullySubmitted && (
                                  <div className="quiz-nav-footer">
                                    <button
                                      className="prev_btn_wrapper"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (qIndex > 0) {
                                          setCurrentQIndex(qIndex - 1);
                                        }
                                      }}
                                      disabled={qIndex === 0}
                                    >
                                      <img
                                        src={assets.ic_left_arrow_blue}
                                        alt="ic_left_arrow_blue"
                                      />
                                      <h5>Previous Question</h5>
                                    </button>
                                    <button
                                      className={
                                        qIndex === questions?.length - 1
                                          ? "end_quiz_btn_inline"
                                          : "nexts_btn_wrapper"
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (qIndex === questions?.length - 1) {
                                          setShowPaletteModal(true);
                                        } else {
                                          setCurrentQIndex(qIndex + 1);
                                        }
                                      }}
                                    >
                                      <h5>
                                        {qIndex === questions?.length - 1
                                          ? "End Quiz"
                                          : "Next Question"}
                                      </h5>
                                      <img
                                        src={assets.ic_arrow_white}
                                        alt="ic_arrow_white"
                                      />
                                    </button>
                                  </div>
                                )}
                              </Fragment>
                            );
                          };

                          if (showStack) {
                            return (
                              <QuizHSlider
                                questions={questions}
                                renderCard={renderQuestionCardInner}
                                currentQIndex={currentQIndex}
                                setCurrentQIndex={setCurrentQIndex}
                                onEndQuiz={() => setShowPaletteModal(true)}
                                endIcon={assets.ic_arrow_white}
                              />
                            );
                          }

                          if (
                            isReviewMode ||
                            (!markDetails?.DETAILS?.length &&
                              questionDetails.length > 0)
                          ) {
                            return (
                              <section
                                className="quiz-container"
                                key={animKey}
                              >
                                <section
                                  className={`quiz-main slide-in-${slideDirection}`}
                                >
                                  {renderQuestionCardInner(currentQIndex)}
                                </section>
                              </section>
                            );
                          }

                          return null;
                        })()}
                      </div>

                      {isReviewMode && (
                        <QuestionPaletteSidebar
                          questions={questions}
                          selectedAnswers={selectedAnswers}
                          getQuestionStatus={getQuestionStatus}
                          currentQIndex={currentQIndex}
                          setCurrentQIndex={setCurrentQIndex}
                          isQuizSubmitted={isFullySubmitted}
                          onSubmitQuiz={() => {
                            setIsQuizActive(false);
                            onSubmitQuiz();
                          }}
                          submitLoading={submitLoading}
                        />
                      )}
                    </div>
                  )}
                </section>
              </section>
            </Fragment>
          )}
        </section>
      </section>
    </Fragment>
  );
};

